import { Link } from 'react-router-dom';
import type { Subject } from '@musicville/shared';
import { Icon, type IconName } from '@/components/brand/Icon';
import { ArrowLink, Section, SectionHeader } from '@/components/ui/primitives';
import { useSubjects } from '@/lib/queries';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/cn';
import { subjectName, subjectText } from '@/lib/catalogue';
import { usePointerTilt } from '@/lib/usePointerTilt';

export function Subjects(): React.JSX.Element {
  const { t } = useI18n();
  const { data, isPending } = useSubjects();

  return (
    <Section>
      <SectionHeader
        overline={t('subjects.overline')}
        title={t('subjects.title', data ? { count: data.length } : undefined)}
        action={<ArrowLink to="/lessons">{t('subjects.all')}</ArrowLink>}
      />

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isPending
          ? Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-[420px] animate-pulse rounded-lg bg-border-subtle" />
            ))
          : data?.map((subject) => <SubjectCard key={subject.slug} subject={subject} />)}
      </div>
    </Section>
  );
}

export function SubjectCard({
  subject,
  className,
}: {
  subject: Subject;
  className?: string | undefined;
}): React.JSX.Element {
  const { t } = useI18n();
  const tiltRef = usePointerTilt<HTMLAnchorElement>();
  return (
    <Link
      ref={tiltRef}
      to={`/lessons/${subject.slug}`}
      className={cn(
        'group relative flex h-[420px] flex-col justify-end overflow-hidden rounded-lg p-6',
        'bg-linear-[119deg,#1e1d38_14%,#080714_83%]',
        'mv-tilt',
        className,
      )}
    >
      <Icon
        name={(subject.icon as IconName) ?? 'piano'}
        size={120}
        className="mv-tilt-depth absolute bottom-24 left-6 text-on-midnight/10 transition-opacity duration-300 group-hover:opacity-70"
      />

      <div className="relative flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="font-display text-h2 font-normal text-on-midnight">
            {subjectName(t, subject)}
          </h3>
          <p className="text-caption text-on-midnight-muted">
            {subjectText(t, subject.slug, 'level', subject.levelLine)}
          </p>
        </div>
        <Icon
          name="arrow-up-right"
          size={24}
          className="text-on-midnight transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </div>
    </Link>
  );
}
